/**
 * `/student/dashboard` — landing page for the student learning center.
 *
 * Shows the student's pseudonym, in-progress course count, and quick
 * links to the Tutor / Assistant pages so the learning-safe affordances
 * are one click away from sign-in.
 */

import { Link } from 'react-router-dom';
import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import { ErrorState, LoadingState } from '../../components/States';

export function StudentDashboard() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';

  const profile = useFetch(
    () => client.getStudentProfile(token),
    [client, token],
  );
  const courses = useFetch(
    () => client.listStudentCourses(token),
    [client, token],
  );

  if (profile.status === 'loading' || courses.status === 'loading') {
    return <LoadingState label="Loading your dashboard…" />;
  }
  if (profile.status === 'error') {
    return <ErrorState error={profile.error} />;
  }
  if (courses.status === 'error') {
    return <ErrorState error={courses.error} />;
  }

  const inProgress = courses.data.filter(
    (c) => c.status === 'in_progress',
  ).length;
  const openAssignments = courses.data.reduce(
    (sum, c) => sum + c.openAssignments,
    0,
  );

  return (
    <section className="page" data-testid="student-dashboard">
      <header className="page__header">
        <h1>
          Welcome,{' '}
          <code aria-label="Pseudonymous student identifier">
            {profile.data.pseudonym}
          </code>
        </h1>
        <p className="muted">
          Your dashboard uses a pseudonym instead of your real name. Tutor and
          Study Assistant guide you through the work — they will not give you
          finished answers.
        </p>
      </header>

      <div className="cards">
        <article className="card card--stat">
          <h2>In-progress courses</h2>
          <p className="card__stat" data-testid="stat-courses">
            {inProgress}
          </p>
          <Link to="/student/courses" className="card__link">
            View courses →
          </Link>
        </article>

        <article className="card card--stat">
          <h2>Open assignments</h2>
          <p className="card__stat" data-testid="stat-assignments">
            {openAssignments}
          </p>
          <Link to="/student/progress" className="card__link">
            See progress →
          </Link>
        </article>

        <article className="card card--stat">
          <h2>Get unstuck (safely)</h2>
          <p className="muted">
            The Tutor will nudge, hint, or walk you through reasoning — never
            hand over a finished solution.
          </p>
          <Link to="/student/tutor" className="card__link">
            Open Homework Tutor →
          </Link>
        </article>
      </div>
    </section>
  );
}
